import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators, NonNullableFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { DataService } from '../../services/data.service';
import { UserLoginDataDTO } from '../../classes/user-account.dto';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ModalHandleLoginErrorsComponent } from '../../components/modals/modal-handle-login-errors/modal-handle-login-errors.component';
import { HttpService } from '../../services/http.service';
import { ModalWithTwoButtonsComponent } from '../../components/modals/modal-with-two-buttons/modal-with-two-buttons.component';

import { GtmService } from '../../services/gtm.service';
import { ConfigService } from '../../services/env-config.service';

@Component({
  selector: 'app-login',
  imports: [
    ReactiveFormsModule,
    NzGridModule,
    TranslateModule,
    NzFlexModule,
    NzTypographyModule,
    NzButtonModule,
    NzDividerModule,
    NzIconModule,
    NzImageModule,
    NzFormModule,
    NzInputModule,
    NzSpaceModule,
    NzModalModule,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LoginComponent implements OnInit, AfterViewInit {
  inputsForm?: FormGroup;
  passwordVisible = false;
  password?: string;
  isVisibleMiddle = false;
  isVisibleMiddleLogin = false;
  isPhonePortrait = false;
  companyId?: string;
  facebookAppId?: string;
  googleAppId?: string;
  showGoogleLoginButton?: boolean;
  disabledMFA?: boolean;
  @ViewChild('forgotPasswordTpl', { static: true }) forgotPasswordTpl!: TemplateRef<any>;
  currentModal?: NzModalRef;
  isOnMobile?: boolean;

  constructor(
    private fb: NonNullableFormBuilder,
    private authService: AuthService,
    private router: Router,
    private dataService: DataService,
    private responsive: BreakpointObserver,
    private translateService: TranslateService,
    private modal: NzModalService,
    private http: HttpService,
    private gtmService: GtmService,
    private configService: ConfigService
  ) {
    if (this.authService.isAuthenticated()) {
      this.router.navigate(['home']).then();
    } else {
      this.authService.isAuthenticatedSubject.next(false);
    }
  }

  public ngOnInit(): void {
    this.isOnMobile = localStorage.getItem('OnMobile') === 'true';
    this.responsive.observe(Breakpoints.HandsetPortrait).subscribe((result) => {
      this.isPhonePortrait = false;
      if (result.matches) {
        this.isPhonePortrait = true;
      }
    });
    this.dataService.config$.subscribe((config) => {
      if (config) {
        this.facebookAppId = config.facebookAppId;
        this.googleAppId = config.googleAppId;
        this.showGoogleLoginButton = config.showGoogleLoginButton;
      }
    });
  }

  forgotPasswordForm: FormGroup = this.fb.group({
    email: this.fb.control('', [Validators.required, Validators.email]),
  });

  validateForm: FormGroup = this.fb.group({
    email: this.fb.control('', [Validators.required, Validators.email]),
    password: this.fb.control('', [Validators.required]),
  });

  protected submitForm(): void {
    this.dataService.companyId$.subscribe((companyId) => {
      this.companyId = companyId;
    });
    if (!this.disabledMFA) {
      window.location.href = `/sso`;
      return;
    } else if (this.disabledMFA ?? true) {
      if (this.validateForm?.valid) {
        const loginData: UserLoginDataDTO = {
          username: this.validateForm.value.email,
          password: this.validateForm.value.password,
          companyId: this.companyId!,
        };
        this.authService.login(loginData).subscribe({
          next: (response): void => {
            this.authService.checkLoginStatus(response, 'login');
            this.gtmService.pushLoginEvent('Sito');
          },
          error: (error): void => {
            this.handleLoginError(error);
          },
        });
      }
    } else {
      Object.values(this.validateForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  private handleLoginError(error: any): void {
    if (error.status === 429) {
      let textsModal = this.translateService.instant('SPA_dialogs.userBlockedTooManyRequests');
      textsModal = textsModal?.replace('%data', error.data);
      this.openModalError(textsModal);
    } else {
      const textsModal = this.translateService.instant('SPA_dialogs.userOrPswError');
      this.openModalError(textsModal, 'credential-error');
    }
  }

  checkEmail(): void {
    const email = this.disabledMFA ? this.validateForm.value.email : this.forgotPasswordForm.value.email;
    if (email) {
      this.http.resetPassword(email).subscribe({
        next: (response) => {
          this.showModalMiddle();
        },
        error: (error) => {
          console.error('Error', error);
          //this.toastr.error(this.translate.instant('toastr.error.activationCodeError'));
        },
      });
    } else {
      if (this.disabledMFA) {
        this.validateForm.get('email')?.markAsDirty();
        this.validateForm.get('email')?.updateValueAndValidity({ onlySelf: true });
      } else {
        this.forgotPasswordForm.get('email')?.markAsDirty();
        this.forgotPasswordForm.get('email')?.updateValueAndValidity({ onlySelf: true });
      }
    }
  }

  goToForgotPassword() {
    this.router.navigate(['/forgot-password']);
  }

  closeModal() {
    this.currentModal?.destroy();
  }

  showModalMiddle(): void {
    this.isVisibleMiddle = true;
  }

  handleOkMiddle(): void {
    console.log('click ok');
    this.isVisibleMiddle = false;
  }

  showModalMiddleLogin(): void {
    this.isVisibleMiddleLogin = true;
  }

  handleOkMiddleLogin(): void {
    console.log('click ok');
    this.isVisibleMiddleLogin = false;
  }

  registrati() {
    this.router.navigate(['/registration']);
  }

  openFacebookLogin() {
    window.open(this.facebookAppId);
  }

  openGoogleLogin() {
    window.open(this.googleAppId);
  }

  private openModalError(text: any, action?: string) {
    const modalRef: NzModalRef<ModalHandleLoginErrorsComponent> = this.modal.create({
      nzContent: ModalHandleLoginErrorsComponent,
      nzClosable: true,
      nzFooter: null,
      nzData: {
        dataModal: {
          texts: {
            title: text.title,
            content: text.content,
            button: text.button,
          },
          actionButton: () => {
            switch (action) {
              case 'credential-error':
                this.validateForm.get('password')?.patchValue('');
                this.validateForm.get('password')?.markAsDirty();
                this.validateForm.get('password')?.updateValueAndValidity({ onlySelf: true });
                this.validateForm.get('email')?.patchValue('');
                this.validateForm.get('email')?.markAsDirty();
                this.validateForm.get('email')?.updateValueAndValidity({ onlySelf: true });
                break;
              default:
                break;
            }
            modalRef.close();
          },
        },
      },
    });
  }

  ngAfterViewInit(): void {
    if (!this.isOnMobile) {
      this.dataService.getConfig().subscribe((res: any) => {
        const googleId: string = res?.googleAppId;
        // @ts-ignore
        google.accounts.id.initialize({
          client_id: googleId,
          callback: (response: any) => this.handleCredentialResponse(response, res),
          use_fedcm_for_prompt: false,
        });
      });
    }
    this.configService.getConfig().subscribe((res: any) => {
      this.disabledMFA = res?.disabledMFA;
    });
  }

  initializeGoogle(): void {
    if (!this.isOnMobile) {
      if (!this.googleAppId) {
        console.error('Google App ID non configurato');
        this.showGoogleLoginError();
        return;
      }

      try {
        // @ts-ignore
        const client = google.accounts.oauth2.initTokenClient({
          client_id: this.googleAppId,
          scope: 'openid email profile',
          callback: (tokenResponse: any) => {
            if (tokenResponse.error) {
              console.error('Errore OAuth:', tokenResponse.error);
              this.showGoogleLoginError();
              return;
            }
            this.fetchGoogleUserInfo(tokenResponse.access_token);
          },
        });
        client.requestAccessToken({
          prompt: 'select_account',
        });
      } catch (error) {
        console.error('Errore inizializzazione OAuth:', error);
        this.showGoogleLoginError();
      }
    }
  }

  private fetchGoogleUserInfo(accessToken: string): void {
    fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Errore risposta API');
        return res.json();
      })
      .then((googleUser) => {
        this.processGoogleLogin(googleUser);
      })
      .catch((err) => {
        console.error('Errore fetch userinfo:', err);
        this.showGoogleLoginError();
      });
  }

  private processGoogleLogin(googleUser: any): void {
    this.http.getGoogleExistUser(googleUser.sub, '45C3200100').subscribe({
      next: () => {
        this.http.getGoogleSSO(googleUser.sub, '45C3200100').subscribe({
          next: () => {
            const cookies = document.cookie.split('; ');
            const cookie = cookies?.find((row) => row.startsWith('googleApiAuthentication='))?.split('=')[1];

            if (cookie) {
              const userData = this.lowercaseFirstLetter(JSON.parse(this.decodeCookie(cookie)));
              this.authService.checkLoginStatus(userData);
              this.gtmService.pushLoginEvent('Google');
            }
          },
          error: () => this.showGoogleLoginError(),
        });
      },
      error: () => this.showGoogleLoginError(),
    });
  }

  private showGoogleLoginError(): void {
    const textsModal = this.translateService.instant('loginGoogleError');
    const modalRef = this.modal.create({
      nzContent: ModalWithTwoButtonsComponent,
      nzClosable: true,
      nzFooter: null,
      nzData: {
        dataModal: {
          texts: { ...textsModal },
          actionButton: () => modalRef.close(),
        },
      },
    });
  }

  handleCredentialResponse(response: any, companyConfig: any) {
    const jwtToken = response.credential;
    if (jwtToken) {
      const googleUser = this.decodeJwt(jwtToken);
      this.http.getGoogleExistUser(googleUser.sub, '45C3200100').subscribe({
        next: (v: any) => {
          this.http.getGoogleSSO(googleUser.sub, '45C3200100').subscribe({
            next: (z: any) => {
              const cookies = document.cookie.split('; ');
              const cookie = cookies?.find((row) => row.startsWith('googleApiAuthentication' + '='))?.split('=')[1];
              if (cookie) {
                const userData = this.lowercaseFirstLetter(JSON.parse(this.decodeCookie(cookie)));
                this.authService.checkLoginStatus(userData);
                this.gtmService.pushLoginEvent('Google');
              }
            },
          });
        },
        error: () => this.showGoogleLoginError(),
      });
    }
  }

  private decodeJwt(jwt: string): any {
    try {
      const base64Url = jwt.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('JWT Decode Error:', error);
      return null;
    }
  }

  protected decodeCookie(token: string) {
    console.log(token.split('.'));
    const base64 = token
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(token.length + ((4 - (token.length % 4)) % 4), '=');

    try {
      return decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
    } catch (e) {
      console.error('Errore durante la decodifica Base64URL:', e);
      return '';
    }
  }

  private lowercaseFirstLetter<T extends Record<string, any>>(obj: T): Record<string, any> {
    return Object.keys(obj).reduce(
      (acc, key) => {
        const newKey = key.charAt(0).toLowerCase() + key.slice(1);
        acc[newKey] = obj[key];
        return acc;
      },
      {} as Record<string, any>
    );
  }
}
